/**
 * Gateway state management (React context + provider).
 *
 * Provides React components with access to gateway state and operations.
 * Uses React context + useReducer for state management.
 *
 * The `GatewayProvider` initializes state from localStorage on mount,
 * syncs changes back to storage, and wires up `GatewayManager` events
 * to dispatch state updates automatically.
 */

import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useMemo,
  createElement,
} from 'react';
import type { ReactNode } from 'react';
import type { GatewayConfig, GatewayStatus } from '../types/gateway';
import {
  loadGateways,
  saveGateways,
  getActiveGatewayId,
  setActiveGatewayId as persistActiveId,
  GatewayManager,
} from './manager';

// ── State ──

export interface GatewayState {
  /** All configured gateways */
  gateways: GatewayConfig[];
  /** ID of the currently active gateway */
  activeGatewayId: string | null;
  /** Live connection status per gateway (runtime, not persisted) */
  connectionStates: Map<string, GatewayStatus>;
  /** Server-advertised capabilities per gateway from hello-ok caps */
  gatewayCaps: Map<string, string[]>;
}

/** Initial state before localStorage hydration */
const initialState: GatewayState = {
  gateways: loadGateways(),
  activeGatewayId: getActiveGatewayId(),
  connectionStates: new Map(),
  gatewayCaps: new Map(),
};

// ── Actions ──

export type GatewayAction =
  | { type: 'SET_GATEWAYS'; gateways: GatewayConfig[] }
  | { type: 'ADD_GATEWAY'; gateway: GatewayConfig }
  | { type: 'UPDATE_GATEWAY'; id: string; updates: Partial<GatewayConfig> }
  | { type: 'REMOVE_GATEWAY'; id: string }
  | { type: 'SET_ACTIVE'; id: string }
  | { type: 'SET_CONNECTION_STATE'; id: string; status: GatewayStatus }
  | { type: 'SET_GATEWAY_CAPS'; id: string; caps: string[] };

/** Gateway state reducer */
export function gatewayReducer(
  state: GatewayState,
  action: GatewayAction,
): GatewayState {
  switch (action.type) {
    case 'SET_GATEWAYS':
      return { ...state, gateways: action.gateways };

    case 'ADD_GATEWAY':
      return {
        ...state,
        gateways: [...state.gateways, action.gateway],
        activeGatewayId: state.activeGatewayId || action.gateway.id,
      };

    case 'UPDATE_GATEWAY':
      return {
        ...state,
        gateways: state.gateways.map((g) =>
          g.id === action.id ? { ...g, ...action.updates } : g,
        ),
      };

    case 'REMOVE_GATEWAY': {
      const filtered = state.gateways.filter((g) => g.id !== action.id);
      const newStates = new Map(state.connectionStates);
      newStates.delete(action.id);
      const newCaps = new Map(state.gatewayCaps);
      newCaps.delete(action.id);
      return {
        ...state,
        gateways: filtered,
        connectionStates: newStates,
        gatewayCaps: newCaps,
        activeGatewayId:
          state.activeGatewayId === action.id
            ? filtered[0]?.id ?? null
            : state.activeGatewayId,
      };
    }

    case 'SET_ACTIVE':
      return { ...state, activeGatewayId: action.id };

    case 'SET_CONNECTION_STATE': {
      const newStates = new Map(state.connectionStates);
      newStates.set(action.id, action.status);
      return { ...state, connectionStates: newStates };
    }

    case 'SET_GATEWAY_CAPS': {
      const newCaps = new Map(state.gatewayCaps);
      newCaps.set(action.id, action.caps);
      return { ...state, gatewayCaps: newCaps };
    }

    default:
      return state;
  }
}

// ── Context ──

export interface GatewayContextValue {
  /** Current gateway state */
  state: GatewayState;
  /** Dispatch reducer actions */
  dispatch: React.Dispatch<GatewayAction>;
  /** The underlying GatewayManager instance (present when using GatewayProvider) */
  manager?: GatewayManager;
}

export const GatewayContext = createContext<GatewayContextValue | null>(null);

/** Hook to access gateway state, dispatch, and manager */
export function useGatewayStore(): GatewayContextValue {
  const ctx = useContext(GatewayContext);
  if (!ctx) {
    throw new Error('useGatewayStore must be used within a GatewayProvider');
  }
  return ctx;
}

// ── Provider ──

export interface GatewayProviderProps {
  /** Child components */
  children: ReactNode;
  /** Optional pre-created GatewayManager (useful for testing) */
  manager?: GatewayManager;
}

/**
 * Gateway state provider.
 *
 * Initializes gateway state from localStorage on mount, syncs changes
 * back to storage, and wires up GatewayManager events to automatically
 * dispatch state updates.
 *
 * @example
 * ```tsx
 * <GatewayProvider>
 *   <App />
 * </GatewayProvider>
 * ```
 */
export function GatewayProvider({ children, manager: externalManager }: GatewayProviderProps) {
  const [state, dispatch] = useReducer(gatewayReducer, initialState);
  const managerRef = useRef<GatewayManager | null>(null);

  // Create or use provided manager
  if (!managerRef.current) {
    managerRef.current = externalManager ?? new GatewayManager();
  }
  const manager = managerRef.current;

  // ── Initialize from localStorage on mount ──
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const gateways = loadGateways();
    const activeId = getActiveGatewayId();
    dispatch({ type: 'SET_GATEWAYS', gateways });
    if (activeId) {
      dispatch({ type: 'SET_ACTIVE', id: activeId });
    }
  }, []);

  // ── Sync gateways to localStorage on changes ──
  const prevGatewaysRef = useRef(state.gateways);
  useEffect(() => {
    // Skip the initial render (we just loaded from storage)
    if (!initializedRef.current) return;
    if (prevGatewaysRef.current === state.gateways) return;
    prevGatewaysRef.current = state.gateways;
    saveGateways(state.gateways);
  }, [state.gateways]);

  // ── Sync activeGatewayId to localStorage on changes ──
  const prevActiveIdRef = useRef(state.activeGatewayId);
  useEffect(() => {
    if (!initializedRef.current) return;
    if (prevActiveIdRef.current === state.activeGatewayId) return;
    prevActiveIdRef.current = state.activeGatewayId;
    if (state.activeGatewayId) {
      persistActiveId(state.activeGatewayId);
    }
  }, [state.activeGatewayId]);

  // ── Wire up GatewayManager events → dispatch ──
  useEffect(() => {
    const unsubStateChange = manager.onStateChange((gatewayId, newState) => {
      dispatch({
        type: 'SET_CONNECTION_STATE',
        id: gatewayId,
        status: newState.status,
      });
      // Always propagate server caps — including empty arrays — so that
      // disconnect/error paths clear stale caps instead of leaving them cached.
      if (newState.caps) {
        dispatch({
          type: 'SET_GATEWAY_CAPS',
          id: gatewayId,
          caps: newState.caps,
        });
      }
    });

    const unsubConnected = manager.onConnected((gatewayId) => {
      dispatch({
        type: 'UPDATE_GATEWAY',
        id: gatewayId,
        updates: {
          status: 'connected',
          lastConnected: new Date().toISOString(),
        },
      });
    });

    const unsubDisconnected = manager.onDisconnected((gatewayId) => {
      dispatch({
        type: 'SET_CONNECTION_STATE',
        id: gatewayId,
        status: 'disconnected',
      });
    });

    const unsubPairing = manager.onPairingRequired((gatewayId) => {
      dispatch({
        type: 'SET_CONNECTION_STATE',
        id: gatewayId,
        status: 'pending',
      });
      dispatch({
        type: 'UPDATE_GATEWAY',
        id: gatewayId,
        updates: { status: 'pending' },
      });
    });

    return () => {
      unsubStateChange();
      unsubConnected();
      unsubDisconnected();
      unsubPairing();
    };
  }, [manager]);

  // ── Cleanup on unmount ──
  useEffect(() => {
    return () => {
      manager.destroy();
    };
  }, [manager]);

  const contextValue = useMemo(
    () => ({ state, dispatch, manager }),
    [state, manager],
  );

  return createElement(GatewayContext.Provider, { value: contextValue }, children);
}
